import { createApiClient, unwrap, type UserProfile } from '@insightai/api-client'
import { getAccessToken, useAuthStore } from '@insightai/auth'
import { config } from './config'

/**
 * F0-56 → F0-59 — the session boundary.
 *
 * SEC-FR-001 split, in one place so it cannot drift:
 *   access token  → Zustand memory, read through a getter on every request
 *   refresh token → HttpOnly Secure SameSite=Strict cookie, set by the backend
 *                   and never visible to JavaScript
 *
 * `onUnauthorized` clears the store rather than redirecting from here. Routing is
 * the router's job; a module that redirects on its own produces two competing
 * navigations when several queries 401 at once.
 */

async function refreshSession(): Promise<string | null> {
  try {
    // Bare fetch, not the api client: routing a refresh through the client's own
    // 401 interceptor would recurse.
    const response = await fetch(`${config.apiBaseUrl}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) return null

    const data = (await response.json()) as { access_token: string; expires_in: number }
    useAuthStore.getState().setSession(data.access_token, data.expires_in)
    return data.access_token
  } catch {
    return null
  }
}

export const api = createApiClient({
  baseUrl: config.apiBaseUrl,
  getAccessToken,
  refresh: refreshSession,
  onUnauthorized: () => useAuthStore.getState().clear(),
})

/** Exchanges the PKCE authorisation code for an access token. */
export async function exchangeCodeForSession(code: string, codeVerifier: string) {
  const result = await api.POST('/api/auth/token', {
    body: { code, code_verifier: codeVerifier, redirect_uri: config.keycloak.redirectUri },
  })
  const tokens = unwrap(result)
  useAuthStore.getState().setSession(tokens.access_token, tokens.expires_in)
  return tokens
}

export async function fetchProfile(): Promise<UserProfile> {
  return unwrap(await api.GET('/api/me', {}))
}

/**
 * Restores a session on cold load.
 *
 * The access token is gone (memory only), so the refresh cookie is the only way
 * back in. Returning `null` rather than throwing keeps "not signed in" an
 * ordinary outcome instead of an error boundary trip.
 */
export async function restoreSession(): Promise<UserProfile | null> {
  /**
   * Prototype mode short-circuit.
   *
   * Without it the console is unusable locally: the access token is memory-only,
   * so a cold load has to exchange the refresh cookie, and with no backend that
   * 404s and the guard redirects to a Keycloak that is not running. See the note
   * on `isPrototypeMode` in config.ts for why this does not weaken SEC-FR-001 —
   * no token is fabricated here, only a profile.
   */
  if (config.prototypeMode) {
    const { mockSessionProfile } = await import('@insightai/mock-api')
    useAuthStore.getState().setUser(mockSessionProfile)
    return mockSessionProfile
  }

  const token = await refreshSession()
  if (!token) {
    useAuthStore.getState().clear()
    return null
  }
  try {
    const profile = await fetchProfile()
    useAuthStore.getState().setUser(profile)
    return profile
  } catch {
    useAuthStore.getState().clear()
    return null
  }
}

export async function logout() {
  try {
    await api.POST('/api/auth/logout', {})
  } finally {
    // Clear locally even if the server call fails: the user asked to leave.
    useAuthStore.getState().clear()
  }
}

/** §7.3 — mints a single-use stream token for EventSource. */
export async function mintStreamToken(resource: string) {
  const result = await api.POST('/api/streams/token', { body: { resource } })
  return unwrap(result).token
}
